import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-customer-support',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumComponent],
  templateUrl: './customer-support.component.html',
  styleUrl: './customer-support.component.scss',
})
export class CustomerSupportComponent implements OnInit, OnDestroy {
  @Input() isStandalone: boolean = true;
  supportBreadcrumbs: any[] = [{ label: 'Customer Support', url: null }];
  // Track whether popup is visible
  showQueryPopup: boolean = false;

  // Track whether order dropdown is expanded
  isOrderDropdownExpanded: boolean = false;

  // Track selected order
  selectedOrderId: string | null = null;

  // Track selected query type
  selectedQueryType: string | null = null;
  isQueryTypeDropdownExpanded: boolean = false;
  queryTypeOptions: string[] = [
    'Technical or Website related',
    'Order Related',
  ];

  // Mobile dropdown popup states
  showMobileQueryTypePopup: boolean = false;
  showMobileOrderPopup: boolean = false;

  // Track callback request success state
  isCallbackRequestSuccessful: boolean = false;

  // Track order list data and loading state
  orderList: any[] = [];
  isLoadingOrders: boolean = false;

  // Track selected order line items
  selectedOrderLineIds: string[] = [];

  sub: Subscription = new Subscription();
  userdata: any;
  orders: any;
  selectedOrder: any;

  // Mobile number binding
  mobileNumber: any;

  // Validation properties
  mobileNumberError: string = '';
  isValidMobileNumber: boolean = true;

  // Track copy state for animation
  isCopied: boolean = false;

  // Track request received state for animation
  showRequestReceived: boolean = false;

  // Track email sent state for animation
  isEmailSent: boolean = false;
  constructor(
    private apiService: ApiService,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.userdata = localStorage.getItem('userData1');
    this.userdata = JSON.parse(this.userdata);
    console.log('userdata', this.userdata);

    // Initialize mobile number from userdata if available
    if (this.userdata && this.userdata.phone_number) {
      this.mobileNumber = this.userdata.phone_number;
    }
  }

  // Button click handlers
  copyPhoneNumber(): void {
    navigator.clipboard.writeText('1800 209 7979').then(() => {
      // Show "Copied" state
      this.isCopied = true;
      this.changeDetectorRef.detectChanges();
      console.log('Phone number copied to clipboard');
      // Reset to "Copy Number" after 1 second
      setTimeout(() => {
        this.isCopied = false;
        this.changeDetectorRef.detectChanges();
      }, 1000);
    });
  }

  sendEmail(): void {
    // Show "Email Sent" state
    this.isEmailSent = true;
    this.changeDetectorRef.detectChanges();

    // Open email client
    window.location.href = 'mailto:Support@circularity.com';

    // Reset to "Send Email" after 1 second
    setTimeout(() => {
      this.isEmailSent = false;
      this.changeDetectorRef.detectChanges();
    }, 1000);
  }

  raiseQuery(): void {
    this.showQueryPopup = true;
    this.disableBodyScroll();
  }

  closeQueryPopup(): void {
    this.showQueryPopup = false;
    // Reset form state when closing
    this.isOrderDropdownExpanded = false;
    this.isQueryTypeDropdownExpanded = false;
    this.selectedOrderId = null;
    this.selectedQueryType = null;
    this.enableBodyScroll();
  }

  toggleOrderDropdown(): void {
    this.isOrderDropdownExpanded = !this.isOrderDropdownExpanded;
  }

  selectOrder(orderId: any): void {
    console.log('orderId', orderId);
    this.selectedOrder = orderId;
    this.selectedOrderId = orderId?.order_number;
    // Reset selected order line items when selecting a new order
    this.selectedOrderLineIds = [];
    // Close dropdown after selection
    this.isOrderDropdownExpanded = false;
  }

  toggleOrderLineItem(itemId: string): void {
    const index = this.selectedOrderLineIds.indexOf(itemId?.toString());
    if (index > -1) {
      // Remove if already selected
      this.selectedOrderLineIds.splice(index, 1);
    } else {
      // Add if not selected
      this.selectedOrderLineIds.push(itemId?.toString());
    }
    console.log('Selected order line IDs:', this.selectedOrderLineIds);
  }

  isOrderLineItemSelected(itemId: string): boolean {
    return this.selectedOrderLineIds.includes(itemId?.toString());
  }

  isFormValid(): boolean {
    // Check if query type is selected
    if (!this.selectedQueryType) {
      return false;
    }

    // If Order Related is selected, check if order and order line items are selected
    if (this.selectedQueryType === 'Order Related') {
      return !!(this.selectedOrder && this.selectedOrderLineIds.length > 0);
    }

    // For other query types, just check if query type is selected
    return true;
  }

  clearOrderSelection(): void {
    this.selectedOrderId = null;
    this.isOrderDropdownExpanded = false;
  }

  onQueryTypeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedQueryType =
      target.value === 'Select a query type' ? null : target.value;

    // Call getorderlist API only when "Order Related" is selected
    if (this.selectedQueryType === 'Order Related') {
      this.fetchOrderList();
    } else {
      // Clear order list if not Order Related
      this.orderList = [];
      this.selectedOrderId = null;
    }
  }

  toggleQueryTypeDropdown(): void {
    this.isQueryTypeDropdownExpanded = !this.isQueryTypeDropdownExpanded;
  }

  selectQueryType(queryType: string): void {
    this.selectedQueryType = queryType;
    this.isQueryTypeDropdownExpanded = false;

    if (this.selectedQueryType === 'Order Related') {
      this.fetchOrderList();
    } else {
      this.orderList = [];
      this.selectedOrderId = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.isQueryTypeDropdownExpanded && !target.closest('.query-type-dropdown')) {
      this.isQueryTypeDropdownExpanded = false;
    }
  }

  // Mobile dropdown methods
  openMobileQueryTypePopup(): void {
    this.showMobileQueryTypePopup = true;
    this.disableBodyScroll();
  }

  closeMobileQueryTypePopup(): void {
    this.showMobileQueryTypePopup = false;
    this.enableBodyScroll();
  }

  selectQueryTypeMobile(queryType: string): void {
    this.selectedQueryType = queryType;
    this.closeMobileQueryTypePopup();

    // Call getorderlist API only when "Order Related" is selected
    if (this.selectedQueryType === 'Order Related') {
      this.fetchOrderList();
    } else {
      // Clear order list if not Order Related
      this.orderList = [];
      this.selectedOrderId = null;
    }
  }

  openMobileOrderPopup(): void {
    this.showMobileOrderPopup = true;
    this.disableBodyScroll();
  }

  closeMobileOrderPopup(): void {
    this.showMobileOrderPopup = false;
    this.enableBodyScroll();
  }

  selectOrderMobile(orderId: any): void {
    this.selectedOrder = orderId;
    this.selectedOrderId = orderId?.order_number;
    this.closeMobileOrderPopup();
  }

  fetchOrderList(): void {
    this.isLoadingOrders = true;

    // Prepare payload for getorderlist API

    const payload = {
      action_type: 'All Orders',
      order_tab: '',
      order_number_search: '',
    };

    this.sub.add(
      this.apiService.getorderlist(payload).subscribe({
        next: (res: any) => {
          if (res.success && res.data) {
            this.orderList = res.data.order_invoice_list;
            console.log('Order list fetched:', this.orderList);
          } else {
            console.log('No orders found or API error:', res.message);
            this.orderList = [];
          }
        },
        error: (error: any) => {
          console.error('Error fetching order list:', error);
          this.orderList = [];
          // Optionally show user-friendly error message
        },
        complete: () => {
          this.isLoadingOrders = false;
        },
      }),
    );
  }

  // Validate Indian mobile number
  validateIndianMobileNumber(mobileNumber: string): boolean {
    // Indian mobile number regex: starts with 6,7,8,9 and followed by 9 digits
    const indianMobileRegex = /^[6-9]\d{9}$/;
    return indianMobileRegex.test(mobileNumber);
  }

  // Handle mobile number changes
  onMobileNumberChange(): void {
    // Clear previous error
    this.mobileNumberError = '';
    this.isValidMobileNumber = true;

    // Validate mobile number if it's not empty
    if (this.mobileNumber && this.mobileNumber?.toString()?.length > 0) {
      if (!this.validateIndianMobileNumber(this.mobileNumber?.toString())) {
        this.mobileNumberError = 'Please enter a valid Indian mobile number';
        this.isValidMobileNumber = false;
        return;
      }
    }

    // Update userdata if it exists and mobile number is valid
    if (this.userdata && this.isValidMobileNumber) {
      this.userdata.phone_number = this.mobileNumber;
      // Optionally update localStorage
      localStorage.setItem('userData1', JSON.stringify(this.userdata));
    }
    console.log('Mobile number updated:', this.mobileNumber);
  }

  requestCallBack(): void {
    console.log('Requesting call back');

    // Validate mobile number before making API call
    if (
      !this.mobileNumber ||
      !this.validateIndianMobileNumber(this.mobileNumber?.toString())
    ) {
      this.mobileNumberError =
        'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)';
      this.isValidMobileNumber = false;
      alert(
        'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)',
      );
      return;
    }

    const payload = {
      action_type: 'Request Call Back',
      contact_no: this.mobileNumber,
    };

    this.loaderService.show();

    this.sub.add(
      this.apiService.requestCallBack(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            // Show "Request Received" animation
            this.showRequestReceived = true;
            this.changeDetectorRef.detectChanges();

            // Reset to "Request Call Back" after 1 second
            setTimeout(() => {
              this.showRequestReceived = false;
              this.changeDetectorRef.detectChanges();
            }, 1000);
          } else {
            this.isCallbackRequestSuccessful = false;
          }
          console.log('res', res);
          this.loaderService.hide();
        },
        error: (error: any) => {
          console.error('error', error);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  getOrderList(): void {
    this.loaderService.show();

    const payload = {
      action_type: 'completed', // ? 'Completed Orders' : 'All Orders',//'invoice_list',
      order_tab: 'completed',
    };

    this.sub.add(
      this.apiService.getorderlist(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.orders = res?.data?.order_invoice_list;
            console.log('Orders loaded:', this.orders);
            // Debug: Check if the fields are present in the first order
          }
          this.loaderService.hide();
        },
        error: (err: any) => {
          console.error('Error fetching order list:', err);
          this.loaderService.hide();
        },
      }),
    );
  }

  submitQuery(): void {
    const queryDescription = (
      document.getElementById('query-description') as HTMLInputElement
    )?.value;

    const payload = {
      action_type: 'Raise Query',
      category_name: this.selectedQueryType,
      orders:
        this.selectedQueryType === 'Order Related'
          ? [
            {
              order_id: this.selectedOrder?.order_id,
              order_line_id: this.selectedOrderLineIds,
            },
          ]
          : [],
      additional_details: queryDescription,
    };

    this.loaderService.show();

    this.sub.add(
      this.apiService.requestCallBack(payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            // Set success state to show the button with checkmark
            this.closeQueryPopup();
          } else {
            // this.closeQueryPopup();
          }
          this.loaderService.hide();
        },
        error: (error: any) => {
          console.error('error', error);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  /**
   * Disable body scroll when popup is open
   */
  private disableBodyScroll(): void {
    if (typeof document !== 'undefined') {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Disable scroll on body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-y', scrollY?.toString());
    }
  }

  /**
   * Enable body scroll when popup is closed
   */
  private enableBodyScroll(): void {
    if (typeof document !== 'undefined') {
      // Restore scroll position
      const scrollY = document.body.getAttribute('data-scroll-y');
      if (scrollY) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.removeAttribute('data-scroll-y');

        // Restore scroll position
        window.scrollTo(0, parseInt(scrollY, 10));
      } else {
        // Fallback: just restore overflow
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    }
  }

  ngOnDestroy(): void {
    // Ensure scroll is enabled when component is destroyed
    this.enableBodyScroll();

    // Unsubscribe from subscriptions
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
